/**
 * Purpose:
 *   Configuration for the sidebar navigation menu items.
 *   Defines the structure, labels, icons, and routes for menu navigation.
 *
 * Notes:
 *   - Icons are located in /public/icons/navigation/
 *   - Items with children are expandable sections
 *   - Items without children are direct navigation links
 *   - Labels are localized in the Menu component with common namespace labelKey values.
 */

import type { MenuItemConfig } from '@/types/menu';
import { ACCOUNT_OVERVIEW_ACTIVE_PATH_PATTERNS } from '@/lib/navigation/accountNavigation';
import { PERMISSIONS } from '@/lib/auth/permissions';

// Re-export type for convenience
export type { MenuItemConfig } from '@/types/menu';

/**
 * Main navigation menu configuration.
 * Modify this array to add, remove, or reorder menu items.
 */
export const MENU_ITEMS: MenuItemConfig[] = [
  {
    id: 'home',
    label: 'Home',
    labelKey: 'menu.home',
    icon: '/icons/navigation/home.svg',
    href: '/',
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions',
    labelKey: 'menu.subscriptions',
    icon: '/icons/navigation/device-sd-card.svg',
    children: [
      {
        id: 'subscriptions-list',
        label: 'List',
        labelKey: 'menu.subscriptions_list',
        icon: '/icons/navigation/list-details.svg',
        href: '/subscriptions',
        requiredPermission: PERMISSIONS.subscriptions.list,
        activePathPatterns: [
          '^/subscriptions/details/[^/]+(?:/(?!activate$).+)?$',
        ],
      },
      {
        id: 'subscriptions-activate-subscription',
        label: 'Activate Subscription',
        labelKey: 'menu.subscriptions_activate',
        icon: '/icons/navigation/clipboard-check.svg',
        href: '/subscriptions/activate',
        requiredPermission: PERMISSIONS.subscriptions.activate,
        activePathPatterns: [
          '^/subscriptions/activate(?:/.*)?$',
          '^/subscriptions/details/[^/]+/activate$',
        ],
      },
      {
        id: 'subscriptions-tags',
        label: 'Tag management',
        labelKey: 'menu.subscriptions_tags',
        icon: '/icons/navigation/tags.svg',
        href: '/subscriptions/tags',
        requiredPermission: PERMISSIONS.tags.list,
      },
    ],
  },
  {
    id: 'product-catalog',
    label: 'Product Catalog',
    labelKey: 'menu.product_catalog',
    icon: '/icons/navigation/list-check.svg',
    href: '/product-catalog',
    requiredPermission: PERMISSIONS.productOfferings.list,
  },
  {
    id: 'background-tasks',
    label: 'Background Tasks',
    labelKey: 'menu.background_tasks',
    icon: '/icons/navigation/logs.svg',
    href: '/background-tasks',
    requiredPermission: PERMISSIONS.backgroundOperations.list,
    activePathPatterns: ['^/background-tasks/[^/]+$'],
  },
  {
    id: 'account',
    label: 'Account Overview',
    labelKey: 'menu.account_overview',
    icon: '/icons/navigation/user.svg',
    href: '/account/overview',
    requiredPermission: PERMISSIONS.accounts.read,
    activePathPatterns: ACCOUNT_OVERVIEW_ACTIVE_PATH_PATTERNS,
  },
  {
    id: 'audit',
    label: 'Audit Trail',
    labelKey: 'menu.audit',
    icon: '/icons/navigation/doc-check.svg',
    href: '/audit',
  },
];
